import styled from "@emotion/styled/macro";
import {
  Button,
  MOBILE_BREAKPOINT,
  TABLET_BREAKPOINT,
  tokens,
} from "@sunrun/experience-ui-components";
import { useMediaQuery } from "react-responsive";
import shareIcon from "components/assets/images/download-icon.svg";
import { FooterOverflowMenu } from "components/molecules/FooterOverflowMenu";

type OfferReviewFooterActionsProps = {
  isCustomer: boolean;
  isMobile: boolean;
  onShareClick: () => void;
  onCheckoutClick: () => void;
  checkoutDisabled?: boolean;
  onUtilityBillClick: () => void;
  isNewHomes: boolean;
  hasUploadedUtilityBill: boolean;
  contractReviewIssueCount?: number;
  onItemsToReviewClick?: () => void;
};

const WIDE_SCREEN = 1440;

const OfferReviewFooterActions = ({
  isCustomer,
  isMobile,
  onShareClick,
  onCheckoutClick,
  checkoutDisabled,
  onUtilityBillClick,
  isNewHomes,
  hasUploadedUtilityBill,
  contractReviewIssueCount,
  onItemsToReviewClick,
}: OfferReviewFooterActionsProps) => {
  const isWideScreen = useMediaQuery({ minWidth: WIDE_SCREEN });

  const showUtilityBillButton = !isNewHomes && !hasUploadedUtilityBill;
  const showContractReviewLink =
    contractReviewIssueCount &&
    contractReviewIssueCount > 0 &&
    !isCustomer &&
    onItemsToReviewClick;
  const showOverflowMenu = isMobile && !isCustomer;

  return (
    <NewFooterWrapper data-testid="new-footer-wrapper">
      <NewFooterActionButtons isMobile={isMobile}>
        <FooterTypography>Take your first step towards Sunrun</FooterTypography>
        <ButtonsWrapper isMobile={isMobile}>
          {showOverflowMenu && (
            <FooterOverflowMenu
              isCustomer={isCustomer}
              onShareClick={onShareClick}
              showUtilityBill={showUtilityBillButton}
              onUtilityBillClick={onUtilityBillClick}
              contractReviewIssueCount={contractReviewIssueCount}
              onItemsToReviewClick={onItemsToReviewClick}
            />
          )}
          {showContractReviewLink && !showOverflowMenu && (
            <ContractReviewLinkWrapper>
              <IssueCountBadge aria-hidden>
                {contractReviewIssueCount}
              </IssueCountBadge>
              <ItemsToReviewButton
                color="link"
                size={isWideScreen ? "md" : "sm"}
                onClick={onItemsToReviewClick}
                data-testid="offer-review-items-to-review"
              >
                Items to review
              </ItemsToReviewButton>
            </ContractReviewLinkWrapper>
          )}
          {showUtilityBillButton && !showOverflowMenu && (
            <Button
              color="secondary"
              onClick={onUtilityBillClick}
              size={isWideScreen ? "md" : "sm"}
            >
              Utility Bill
            </Button>
          )}
          {!isCustomer && !showOverflowMenu && (
            <ShareButton color="secondary" onClick={onShareClick}>
              <ShareIconImg src={shareIcon} alt="Share offer" />
            </ShareButton>
          )}
          <Button
            onClick={onCheckoutClick}
            size={isWideScreen ? "md" : "sm"}
            disabled={checkoutDisabled}
          >
            Go to checkout
          </Button>
        </ButtonsWrapper>
      </NewFooterActionButtons>
    </NewFooterWrapper>
  );
};

const NewFooterWrapper = styled.div`
  padding: 12px 64px 24px;
  background-color: ${tokens.TINTS_CHARGE_BLUE_50};
  width: 100%;
  position: sticky;
  z-index: 10;
  bottom: 0;
  box-sizing: border-box;

  @media (min-width: ${WIDE_SCREEN}px) {
    padding: 16px 64px 24px;
  }

  @media (max-width: ${TABLET_BREAKPOINT}px) {
    padding: 16px 48px 12px;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 24px 16px;
  }
`;

const NewFooterActionButtons = styled.div<{ isMobile: boolean }>`
  max-width: 1005px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin: 0 auto;
  gap: ${({ isMobile }) => (isMobile ? "16px" : "0px")};
`;

const FooterTypography = styled.p`
  color: ${tokens.BRAND_HEROBLUE};
  font-size: 24px;
  font-weight: 500;
  line-height: 110%;
  letter-spacing: -0.64px;
  margin: 0;

  @media (min-width: ${WIDE_SCREEN}px) {
    font-size: 28px;
  }

  @media (max-width: ${TABLET_BREAKPOINT}px) {
    font-size: 20px;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    font-size: 16px;
  }
`;

const ShareButton = styled(Button)`
  border-radius: 80px;
  display: flex;
  width: 40px;
  height: 40px;
  padding: 8px 12px !important;
  align-self: center;
  justify-content: center;

  @media (min-width: ${WIDE_SCREEN}px) {
    width: 60px;
    height: 60px;
  }
`;

const ShareIconImg = styled.img`
  @media (min-width: ${WIDE_SCREEN}px) {
    width: 24px;
    height: 24px;
  }
`;

const ButtonsWrapper = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-direction: row;
  gap: ${({ isMobile }) => (isMobile ? "8px" : "16px")};
  width: fit-content;
  flex-shrink: 0;
`;

const ContractReviewLinkWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const IssueCountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: ${tokens.BRAND_HEROBLUE};
  color: ${tokens.WHITE};
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
  margin-top: 2px;
`;

const ItemsToReviewButton = styled(Button)`
  &.sr-button.sr-button-link {
    text-decoration: unset;
    white-space: nowrap;
    border-bottom: 1px solid ${tokens.BRAND_HEROBLUE};
  }
`;

export { OfferReviewFooterActions };
